import { PaymentTypes } from 'appConstants';
import { format, parse } from 'date-fns';

export type ISubscription = {
  id: number;
  subject: string;
  price: string;
  subscription: string;
  fromDate: string;
  toDate: string;
  renewalDate: string;
  isFreeTrial: boolean;
  isRenewable: boolean;
  isUpgradable: boolean;
  subjectId: number;
  trialRemainingDays: number | null;
};

export type SubscriptionsList = ISubscription[];

export type UpgradeSubscription = {
  packageId: number;
  subjectId: number;
  price: number;
  subscription: string;
  type: string;
};

export interface UpgradePackageInfo {
  packageId: number;
  subjectId: number;
  price: number;
  subscription: string;
  accessLength: number;
  type: string;
}

export interface UpgradeSubscriptionFormValues {
  poNumber: string;
  paymentType: PaymentTypes.PAY_BY_CARD | PaymentTypes.PAY_BY_INVOICE;
  package: UpgradePackageInfo | null;
  cardholderName: string;
  card: string;
  validPromoCode: string;
  total: number;
  validDiscount: number;
  FinanceEmail: string;
}

export interface RenewSubscriptionFormValues {
  poNumber: string;
  paymentType: PaymentTypes.PAY_BY_CARD | PaymentTypes.PAY_BY_INVOICE;
  cardholderName: string;
  card: string;
  validPromoCode: string;
  total: number;
  validDiscount: number;
  subDuration: string;
  FinanceEmail: string;
}

export interface accessInformation {
  bCanAddFreeTrial: boolean;
  bCanAddSubscription: boolean;
}

export type UpgradeSubscriptionsList = UpgradeSubscription[];

export class SubscriptionsParser {
  public static parseGetSubscriptionList = (response: SubscriptionsList) => {
    return response.map((s: ISubscription) => ({
      ...s,
      fromDate: s.fromDate
        ? format(parse(s.fromDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
        : '',
      toDate: s.toDate
        ? format(parse(s.toDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
        : '',

      renewalDate: s.renewalDate
        ? format(parse(s.renewalDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
        : '',
    }));
  };

  public static createEmptyUpgradePackages: (FinanceEmail: any) => UpgradeSubscriptionFormValues =
    (FinanceEmail) => ({
      poNumber: '',
      paymentType: PaymentTypes.PAY_BY_CARD,
      package: null,
      cardholderName: '',
      card: '',
      validPromoCode: '',
      total: 0,
      validDiscount: 0,
      FinanceEmail: FinanceEmail ? FinanceEmail : '',
    });

  public static createEmptyRenewPackages: (
    SubOption: string,
    FinanceEmail: string | undefined
  ) => RenewSubscriptionFormValues = (SubOption, FinanceEmail) => ({
    poNumber: '',
    paymentType: PaymentTypes.PAY_BY_CARD,
    cardholderName: '',
    card: '',
    validPromoCode: '',
    total: 0,
    validDiscount: 0,
    subDuration: SubOption,
    FinanceEmail: FinanceEmail ? FinanceEmail : '',
  });

  public static buildUpgradeByInvoicePayload = (
    values: UpgradeSubscriptionFormValues
  ) => ({
    promoCode: values.validPromoCode,
    packageIDs: [values.package?.packageId],
    expectedGrossAmount: values.total,
    poNumber: values.poNumber,
    FinanceEmail: values.FinanceEmail,
  });

  public static buildUpgradeByCardPayload = (
    values: UpgradeSubscriptionFormValues,
    token: any
  ) => ({
    promoCode: values.validPromoCode,
    packageIDs: [values.package?.packageId],
    expectedGrossAmount: values.total,
    nameOnCard: token.card.name,
    cardEndDigits: token.card.last4,
    expiryDate: `${
      token.card.exp_month.toString().length === 1 && '0'
    }${token.card.exp_month.toString()}/${token.card.exp_year.toString()[2]}${
      token.card.exp_year.toString()[3]
    }`,
    stripeToken: token.id,
    poNumber: values.poNumber,
    FinanceEmail: values.FinanceEmail,
  });

  public static buildRenewByInvoicePayload = (
    values: RenewSubscriptionFormValues,
    packageIDs: number[],
    expectedGrossAmount: number
  ) => ({
    promoCode: values.validPromoCode,
    packageIDs,
    expectedGrossAmount,
    poNumber: values.poNumber,
    FinanceEmail: values.FinanceEmail,
  });

  public static buildRenewByCardPayload = (
    values: RenewSubscriptionFormValues,
    token: any,
    expectedGrossAmount: number,
    packageIDs: number[]
  ) => ({
    promoCode: values.validPromoCode,
    packageIDs,
    expectedGrossAmount,
    nameOnCard: token.card.name,
    cardEndDigits: token.card.last4,
    expiryDate: `${
      token.card.exp_month.toString().length === 1 && '0'
    }${token.card.exp_month.toString()}/${token.card.exp_year.toString()[2]}${
      token.card.exp_year.toString()[3]
    }`,
    stripeToken: token.id,
    poNumber: values.poNumber,
    FinanceEmail: values.FinanceEmail,
  });

  public static buildRenewAfter3DSecurePayload = (response: any) => {
    return {
      tid: response.tid,
      stripePaymentIntentID: response.error
        ? response.error.payment_intent.id
        : response.paymentIntent.id,
    };
  };

  public static parseError = (errors: any) => {
    let error: string;
    switch (errors[0].messageCode) {
      case 'Invalid_PromoCode_Expired':
        error = 'Promo code is expired';
        break;
      case 'ExpectedGrossAmount':
        error = 'Sorry, something went wrong. Please try again. ';
        break;
      case 'pkey_general_payment_subscription_failed':
        error =
          'Something went wrong when processing the payment. Please try again.';
        break;
      default:
        error = 'Sorry, something went wrong. Please try again. ';
    }
    return error;
  };
}
